"use client";

import { Card } from "@/components/ui/card";
import { FileText, Shield, Scale, Clock, AlertTriangle, Users, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";

export default function TermsOfServicePage() {
  const router = useRouter();
  const lastUpdated = "November 25, 2025";

  return (
    <div className="container max-w-3xl mx-auto p-4 pb-32 space-y-6">
      <div className="mb-4 flex items-center">
        <Button 
          variant="outline" 
          size="sm" 
          className="text-muted-foreground hover:text-foreground border-muted-foreground/30 hover:border-muted-foreground/50"
          onClick={() => router.back()}
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-center mb-2 text-primary">Terms of Service</h1>
      <p className="text-center text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

      <div className="prose prose-sm max-w-none text-foreground">
        <p>
          Welcome to BatchFinder. Please read these Terms of Service ("Terms") carefully as they contain important information about your legal rights, remedies, and obligations. By accessing or using BatchFinder, you agree to comply with and be bound by these Terms.
        </p>
      </div>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <FileText className="text-primary" />
          <h2>1. Acceptance of Terms</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            By accessing or using BatchFinder, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use BatchFinder.
          </p>
          <p>
            We reserve the right to modify these Terms at any time. Your continued use of BatchFinder after any such changes constitutes your acceptance of the new Terms.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Shield className="text-primary" />
          <h2>2. Privacy Policy</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            Our{" "}
            <Link href="/privacy-policy" className="text-primary hover:underline font-medium">
              Privacy Policy
            </Link>{" "}
            describes how we handle the information you provide to us when you use BatchFinder. By using BatchFinder, you consent to our collection and use of personal data as outlined in our Privacy Policy.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Users className="text-primary" />
          <h2>3. User Accounts</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            To use certain features of BatchFinder, you may be required to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </p>
          <p>
            You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
          </p>
          <p>
            We reserve the right to suspend or terminate your account if any information provided proves to be inaccurate, not current, or incomplete.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <MessageSquare className="text-primary" />
          <h2>4. SMS/Text Messaging Terms</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p className="font-semibold text-foreground">
            Consent to Receive Text Messages
          </p>
          <p>
            By providing your mobile phone number and opting in during account registration, you expressly consent to receive automated text messages (SMS) from BatchFinder at the mobile number you provided. You confirm that you are the subscriber to the relevant phone number or are authorized to consent on behalf of the subscriber.
          </p>
          
          <p className="font-semibold text-foreground mt-4">
            Types of Messages
          </p>
          <p>
            You may receive various types of automated text messages from BatchFinder, including but not limited to:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Verification codes for account authentication</li>
            <li>Batch notifications and alerts</li>
            <li>Account-related updates and important service announcements</li>
            <li>Promotional messages (if separately opted in)</li>
          </ul>

          <p className="font-semibold text-foreground mt-4">
            Message Frequency
          </p>
          <p>
            Message frequency varies based on your account activity and settings. You may receive multiple messages per day depending on batch availability and your notification preferences.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Message and Data Rates
          </p>
          <p>
            Message and data rates may apply. BatchFinder does not charge for text messages, but your mobile carrier's standard messaging rates will apply. Please contact your mobile carrier for details about your messaging plan and any applicable charges.
          </p>

          <p className="font-semibold text-foreground mt-4">
            How to Opt-Out
          </p>
          <p>
            You can opt-out of receiving text messages at any time by:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Texting <span className="font-semibold">STOP</span> to any message you receive from us</li>
            <li>Adjusting your notification settings in your BatchFinder account</li>
            <li>Contacting us at avalon.batchfinder@gmail.com</li>
          </ul>
          <p>
            After you opt-out, you will receive one final confirmation message. Please note that opting out of text messages may affect your ability to receive important account notifications and batch alerts.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Help and Support
          </p>
          <p>
            For help or support with text messages, text <span className="font-semibold">HELP</span> to any message you receive from us, or contact us at avalon.batchfinder@gmail.com.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Supported Carriers
          </p>
          <p>
            Our SMS service is available on all major U.S. carriers, including but not limited to AT&T, Sprint, T-Mobile, Verizon, Boost, Cricket, MetroPCS, U.S. Cellular, Virgin Mobile, and others. Service may not be available on all carriers or in all areas.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Privacy and Data Use
          </p>
          <p>
            We respect your privacy. Your phone number and text message interactions are handled in accordance with our Privacy Policy. We will not share your phone number with third parties for their marketing purposes without your explicit consent.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Terms May Change
          </p>
          <p>
            We reserve the right to modify or discontinue our SMS service at any time. We may also update these SMS terms, and will notify you of material changes via text message or email. Your continued participation in our SMS program after such notice constitutes acceptance of the updated terms.
          </p>

          <p className="font-semibold text-foreground mt-4">
            No Liability for Carrier Issues
          </p>
          <p>
            BatchFinder is not liable for any delays or failures in message delivery that are caused by your mobile carrier or any other third-party service provider. We do not guarantee that messages will be delivered or received in a timely manner.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Scale className="text-primary" />
          <h2>5. License and Restrictions</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, and revocable license to access and use BatchFinder for your personal, non-commercial use.
          </p>
          <p>
            You may not:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Use BatchFinder for any illegal purpose or in violation of any local, state, national, or international law</li>
            <li>Violate or encourage others to violate the rights of third parties, including intellectual property rights</li>
            <li>Interfere with or disrupt BatchFinder or servers or networks connected to BatchFinder</li>
            <li>Attempt to gain unauthorized access to BatchFinder, user accounts, or computer systems</li>
            <li>Harvest, collect, or gather user data without permission</li>
            <li>Use BatchFinder to transmit viruses or other malicious code</li>
          </ul>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Clock className="text-primary" />
          <h2>6. Subscription and Billing</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            BatchFinder offers various subscription plans. By selecting a subscription plan, you agree to pay the subscription fees indicated for that service.
          </p>
          <p>
            Subscription fees are billed in advance on a recurring basis. You authorize us to charge your payment method for all fees incurred in connection with your account.
          </p>
          <p>
            You may cancel your subscription at any time. Upon cancellation, your subscription will remain active until the end of the current billing period.
          </p>
          <p>
            We reserve the right to change our subscription fees at any time. If we change our fees, we will provide notice of the change on the website or via email at least 30 days before the change takes effect.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <AlertTriangle className="text-primary" />
          <h2>7. Disclaimer of Warranties</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            BATCHFINDER IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
          </p>
          <p>
            WE DO NOT WARRANT THAT BATCHFINDER WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT BATCHFINDER OR THE SERVERS THAT MAKE IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <FileText className="text-primary" />
          <h2>8. Limitation of Liability</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL BATCHFINDER, ITS AFFILIATES, DIRECTORS, EMPLOYEES, OR LICENSORS BE LIABLE FOR ANY DIRECT, INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, THAT RESULT FROM THE USE OF, OR INABILITY TO USE, BATCHFINDER.
          </p>
          <p>
            UNDER NO CIRCUMSTANCES WILL BATCHFINDER BE RESPONSIBLE FOR ANY DAMAGE, LOSS, OR INJURY RESULTING FROM HACKING, TAMPERING, OR OTHER UNAUTHORIZED ACCESS OR USE OF BATCHFINDER OR YOUR ACCOUNT OR THE INFORMATION CONTAINED THEREIN.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Scale className="text-primary" />
          <h2>9. Governing Law</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
          </p>
          <p>
            Any dispute arising from or relating to these Terms or your use of BatchFinder shall be resolved exclusively in the courts located in the United States.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <FileText className="text-primary" />
          <h2>10. Contact Information</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p className="font-medium">
            avalon.batchfinder@gmail.com
          </p>
        </div>
      </Card>

      <div className="mt-8 text-center">
        <p className="text-muted-foreground text-sm">
          By using BatchFinder, you acknowledge that you have read, understood, and agree to be bound by these <Link href="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link>.
        </p>
      </div>
    </div>
  );
}
